import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, ArrowRight, Shield, Airplane,
  Users, SealCheck, CalendarBlank,
  FileText, Star, CurrencyDollar, ArrowsClockwise,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, my UAE visit visa is expiring soon and I need to extend/renew it without leaving the country. Please advise the process and cost.'
);

const SUMMARY_CARDS = [
  { label: 'No Exit Needed', value: 'Extend Inside UAE', sub: 'No visa-run flight required', Icon: ArrowsClockwise },
  { label: 'Processing Time', value: '1 – 2 Working Days', sub: 'Before your visa expires', Icon: Clock },
  { label: 'Extra Stay', value: 'Usually +30 Days', sub: 'Depends on visa type', Icon: CalendarBlank },
  { label: 'Who Can Apply', value: 'Visit Visa Holders', sub: 'Before or after expiry', Icon: Users },
  { label: 'Apply From', value: 'Abu Dhabi / online', sub: 'No immigration office visit', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: Clock,     label: 'Visitors Nearing Visa Expiry' },
  { Icon: ArrowsClockwise, label: 'Anyone Wanting to Avoid a Visa Run' },
  { Icon: FileText,  label: 'Visa Status Change Applicants' },
  { Icon: Warning,   label: 'Travellers Who Have Already Overstayed' },
  { Icon: Users,     label: 'Family Visitors Extending Their Trip' },
  { Icon: MapPin,    label: 'Abu Dhabi, Dubai & Northern Emirates Visitors' },
];

const EXTENSION_TABLE = [
  { type: '30-Day Single/Multiple Entry Visa', extendable: 'Yes', period: '+30 days (commonly once)', badge: 'blue' },
  { type: '60-Day Single/Multiple Entry Visa', extendable: 'Yes', period: '+30 days (commonly once)', badge: 'blue' },
  { type: '5-Year Multiple Entry Visa', extendable: 'Per-visit stay only', period: 'Re-enter for a fresh 90-day count', badge: 'navy' },
  { type: '96-Hour Transit Visa', extendable: 'Not extendable', period: 'Exit required at expiry', badge: 'gray' },
  { type: 'Already Overstayed', extendable: 'Fine clearance first', period: 'Then extend or exit', badge: 'green' },
];

const HIDDEN_FACTS = [
  {
    Icon: Shield,
    title: 'Extension eligibility depends on the exact visa sub-type',
    desc: 'Not every "visit visa" is the same behind the scenes — single-entry, multiple-entry and sponsor type can all affect whether an extension is possible. We check your exact visa, not just its headline duration, before applying.',
  },
  {
    Icon: CurrencyDollar,
    title: 'The overstay fine clock starts the day after expiry',
    desc: 'There is no grace period once your visa lapses — the daily fine begins immediately. Extending or exiting a day early is always cheaper than waiting one day too long.',
  },
  {
    Icon: ArrowsClockwise,
    title: 'A visa run isn’t always cheaper than an extension',
    desc: 'Flight costs, time lost, and the fact that re-entry is always at immigration’s discretion often make an in-country extension the better option — even before comparing the actual extension fee.',
  },
  {
    Icon: FileText,
    title: 'Status change can sometimes replace an extension entirely',
    desc: 'If your circumstances have changed — a new job offer, marriage, or a change of sponsor — switching your visa status inside the UAE may solve the problem better than a simple extension. We flag this option when relevant.',
  },
];

const EXTENSION_SERVICES = [
  { label: 'UAE Visit Visa',        to: '/visas/uae-visit-visa', desc: 'New visa applications' },
  { label: 'Job Seeker Visa',       to: '/visas/job-seeker-visa', desc: 'Convert to job search status' },
  { label: 'Family Visa',           to: '/visas/family-visa',    desc: 'Sponsor spouse & children' },
  { label: 'Flights',               to: '/flights',              desc: 'If exit is needed instead' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Visa Extension & Renewal Assistance',
  description: 'UAE visit visa extension and renewal assistance without exit — Abu Dhabi and Dubai, avoiding overstay fines. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/visa-extension',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Visa Extension & Renewal',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function VisaExtensionExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Visa Extension — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For visitors in Abu Dhabi and Dubai whose UAE visa is close to expiry and want more time without leaving the country.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="Can I extend my UAE visit visa without leaving the country?"
          answer="In most cases, yes. Standard 30-day and 60-day UAE visit visas can typically be extended from inside the UAE — commonly by another 30 days — without an exit-and-re-entry visa run. Eligibility depends on the exact visa sub-type, so it should be checked before applying. Big Bird Travel, based in Mussafah, Abu Dhabi, handles extension applications for visitors across the UAE, and processing is usually 1–2 working days."
        />
        <AIAnswerBox
          question="What happens if I overstay my UAE visa?"
          answer="A daily fine applies starting the day after the visa expires, with no grace period. The longer it is left, the larger the fine and the more complex the clearance. Applying for an extension a few days before expiry — or clearing an existing overstay quickly — is always the cheaper, lower-risk path."
        />
      </div>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Extension Eligibility by Visa Type</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            Which UAE visas can be extended — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Rules vary by exact visa sub-type and sponsor — confirm your specific case with us before applying.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[600px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Your Current Visa</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Extendable?</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl">Typical Outcome</th>
                </tr>
              </thead>
              <tbody>
                {EXTENSION_TABLE.map((row, i) => (
                  <tr key={row.type} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.type}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'green' ? 'bg-green-100 text-green-800' :
                        row.badge === 'blue'  ? 'bg-[#EAF7FF] text-[#0068B3]' :
                        row.badge === 'navy'  ? 'bg-[#071126] text-white' :
                                                'bg-gray-100 text-gray-600'
                      }`}>{row.extendable}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-gray-400">
            * Confirm your exact visa sub-type on{' '}
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="text-[#0068B3] underline underline-offset-2">WhatsApp</a> before applying.
          </p>
        </div>
      </section>

      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Timing is everything with visa extensions
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The biggest risk isn't the extension itself — it's applying too late. Once a visa expires, the daily overstay fine starts immediately, and clearing it takes longer than a routine extension.
            </p>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-6">
              We recommend starting the process at least 3–5 days before your current visa expires.
            </p>
            <div className="space-y-2.5">
              <ExpandableTip title="Apply before expiry, not after">
                Extensions submitted a few days before the current visa lapses process smoothly. Waiting until the last day — or after expiry — adds the overstay fine and extra clearance steps.
              </ExpandableTip>
              <ExpandableTip title="Check if your visa is extendable at all">
                Not every visa type qualifies — some short-validity or sponsor-specific visas cannot be extended and require an exit. We check this first so you don't pay for an application that can't succeed.
              </ExpandableTip>
              <ExpandableTip title="Already overstayed? Don't wait longer">
                The fine grows daily. Message us immediately with your visa copy so we can calculate the exact fine and the fastest legal path forward.
              </ExpandableTip>
              <ExpandableTip title="Consider a status change instead">
                If your situation has changed (new job, marriage, sponsor change), switching your visa status may be a better fit than a simple extension. We advise on the right route.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Status Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: '30 or 60-day visa, not yet expired', result: 'Extend now, fast process', type: 'ok' },
                { text: 'Visa expires in the next few days', result: 'Apply today to avoid fines', type: 'warn' },
                { text: 'Already overstayed', result: 'Message us immediately', type: 'warn' },
                { text: '5-year multiple entry visa', result: 'Re-enter instead of extending', type: 'ok' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Visa — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your visa copy — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most extension guides don't tell you
          </h2>
          <p className="text-blue-50/60 text-[14px] mb-8 max-w-2xl">
            The basics are everywhere. These are the details that decide whether your extension is approved smoothly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Visa expiring soon?</p>
              <p className="text-blue-50/60 text-[13px]">Send your visa copy and expiry date. We'll confirm if it's extendable and the fastest way to avoid a fine — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Extensions handled fast, before the clock runs out
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your visa copy today. We'll confirm if it's extendable, apply immediately, and keep you clear of any overstay fine."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For extensions we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Check your exact visa sub-type for extension eligibility.',
                'Calculate any overstay fine and the fastest path to clear it.',
                'Submit and track the extension application for you.',
                'Advise on status-change alternatives where relevant.',
                'Flag your next expiry date so this never repeats.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "My visa was expiring in 3 days and I panicked. They extended it the same day and explained exactly what to do next time."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Visitor, Dubai</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Related Visa Services</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Extension sorted. Here's what else we can help with.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {EXTENSION_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We process visa extensions for visitors across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office, with real one-on-one help.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function VisaExtension() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['visa-extension']}
      extraSections={<VisaExtensionExtraSections />}
    />
  );
}
